/**
 * Helper: Trustpilot
 * -----------------------------------------------------------------------------
 * Handles Trustpilot responses.
 * - Add in alphabetical order.
 *
 */

/**
 * Set global variables.
 */
const trustpilotCacheKey = 'cnvs-trustpilot-cache'

/**
 * Perform two calls to Trustpilot's API.
 * - One to check if there's any legacy star data (i.e. from Yotpo).
 * - One to check if there's any current star data (i.e. from Trustpilot).
 * @param {Array} skus - Array of sku to check.
 * @returns {Object}
 */
export default (skus, useCache = cnvs.settings.queryCache.duration) => {
  return new Promise(async(resolve) => {
    const ratingData = {
      imported: 0,
      total: 0,
      totalImported: 0,
      trustpilot: 0,
    }

    const { apiKey, businessUnitId } = cnvs.credentials.trustpilot
    if (!apiKey || !businessUnitId || !skus.length) {
      resolve(ratingData)
      return
    }

    const storageKey = skus.join('-')
    const trustpilotCache = JSON.parse(sessionStorage.getItem(trustpilotCacheKey))
    const currentTime = new Date().getTime()

    if (
      useCache &&
      !cnvs.settings.queryCache.disable &&
      !cnvs.storybook &&
      trustpilotCache &&
      trustpilotCache[storageKey]
    ) {
      let cacheExpired = false

      if (typeof useCache === 'number' && trustpilotCache[storageKey].created) {
        const difference = currentTime - trustpilotCache[storageKey].created
        cacheExpired = difference > useCache
      }

      if (!cacheExpired) {
        resolve(trustpilotCache[storageKey].data)
        return
      }
    }

    const importedApiUrl = `https://api.trustpilot.com/v1/product-reviews/business-units/${businessUnitId}/imported-reviews-summaries?sku=${skus.toString()}&apikey=${apiKey}`
    const nativeApiUrl = `https://api.trustpilot.com/v1/product-reviews/business-units/${businessUnitId}?sku=${skus.toString()}&apikey=${apiKey}`

    /**
     * Check for legacy review data (e.g. via Yotpo).
     */
    const promiseImported = new Promise((resolveChild, reject) => {
      fetch(importedApiUrl)
        .then(response => response.json())
        .then(response => {
          ratingData.imported = response.starsAverage
          ratingData.totalImported = response.numberOfReviews?.total
          resolveChild(response)
        })
        .catch((error) => {
          reject(error)
        })
    })

    /**
     * Check for most recent review data (e.g. via Trustpilot).
     */
    const promiseNative = new Promise((resolveChild, reject) => {
      fetch(nativeApiUrl)
        .then(response => response.json())
        .then(response => {
          ratingData.trustpilot = response.starsAverage
          ratingData.total = response.numberOfReviews?.total
          resolveChild(response)
        })
        .catch((error) => {
          reject(error)
        })
    })

    /**
     * Merge responses.
     */
    await Promise.all([promiseImported, promiseNative])

    /**
     * Set response to cache.
     */
    if (cacheData(ratingData, useCache)) {
      try {
        const data = JSON.parse(sessionStorage.getItem(trustpilotCacheKey)) || {}

        data[storageKey] = {
          created: currentTime,
          data: ratingData,
        }

        sessionStorage.setItem(trustpilotCacheKey, JSON.stringify(data))

      } catch (error) {
        reportError(error)
      }
    }

    resolve(ratingData)
  })
}

/**
 * Returns whether response data should be cached.
 * @param {Object} response - Response data.
 * @param {Boolean} useCache - Use cache theme setting.
 * @returns {Boolean}
 */
function cacheData(response, useCache) {
  if (!useCache || cnvs.storybook || !response) {
    return false
  }

  /**
   * Do not cache if response data has a single falsey property.
   * - Response returns as { [property]: undefined } so just checking object
   *   will always return truthy.
   */
  return !(
    Object.keys(response)?.length === 1 &&
    !response[Object.keys(response)[0]]
  )
}
